import React, { useMemo, useState } from 'react';
import {
  Box,
  Text,
  Loader,
  Alert,
  Paper,
  Card,
  Group,
  Stack,
  Table,
  ScrollArea,
  ActionIcon,
  TextInput,
  Select,
  Button,
  Radio,
} from '@mantine/core';
import { DatePickerInput, MonthPickerInput } from '@mantine/dates';
import { IconSearch, IconDownload, IconArrowLeft, IconInfoCircle, IconExternalLink } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';

const ColorLegend = () => (
  <Card withBorder radius="md" style={{ width: '350px' }}>
    <Text size="sm" fw={500} mb="xs">
      Legend
    </Text>
    <Stack gap="xs">
      {[
        { color: '#73A57D', label: '-2% or more' },
        { color: '#D1A752', label: '-2% - -5%' },
        { color: '#D88C8C', label: 'Less than -5%' },
      ].map(({ color, label }) => (
        <Group key={label} gap="xs" align="flex-start">
          <Box
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: color,
              borderRadius: '2px',
              flexShrink: 0,
              marginTop: '2px',
            }}
          />
          <Text size="xs" c="dimmed" style={{ flex: 1, lineHeight: 1.3 }}>
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

function getPreviousQuarters(count = 8) {
  const today = new Date();
  const currentQuarter = Math.floor(today.getMonth() / 3) + 1;
  let year = today.getFullYear();
  let quarter = currentQuarter;
  const quarters = [];
  for (let i = 0; i < count; i++) {
    quarters.push({ year, quarter });
    quarter--;
    if (quarter === 0) { quarter = 4; year--; }
  }
  return quarters;
}

function getQuarterDates(year, quarter) {
  const startMonth = (quarter - 1) * 3;
  const from = new Date(year, startMonth, 1);
  const to = new Date(year, startMonth + 3, 0);
  return {
    fromDate: from.toISOString().split('T')[0],
    toDate: to.toISOString().split('T')[0],
  };
}

function lastDayOfPreviousMonth() {
  const d = new Date();
  d.setDate(0);
  return d;
}

function toISODate(date) {
  return date ? date.toISOString().split('T')[0] : null;
}

const SalesPerformanceScorecardHistoricalPricingDetail = () => {
  const { ccode } = useParams();
  const navigate = useNavigate();

  const [mode, setMode] = useState('quarter');

  const quarterOptions = useMemo(
    () =>
      getPreviousQuarters(8).map(({ year, quarter }) => ({
        value: `${year}-${quarter}`,
        label: `Q${quarter} ${year}`,
      })),
    []
  );
  const [selectedQuarter, setSelectedQuarter] = useState(quarterOptions[0].value);

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d;
  });

  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  const { fromDate, toDate, periodLabel } = useMemo(() => {
    if (mode === 'quarter') {
      const [y, q] = selectedQuarter.split('-').map(Number);
      const { fromDate, toDate } = getQuarterDates(y, q);
      return { fromDate, toDate, periodLabel: `Q${q} ${y}` };
    }
    if (mode === 'month' && selectedMonth) {
      const y = selectedMonth.getFullYear();
      const m = selectedMonth.getMonth();
      const from = new Date(y, m, 1);
      const to = new Date(y, m + 1, 0);
      const label = from.toLocaleString('default', { month: 'long', year: 'numeric' });
      return { fromDate: toISODate(from), toDate: toISODate(to), periodLabel: label };
    }
    if (mode === 'custom' && customFrom && customTo) {
      return {
        fromDate: toISODate(customFrom),
        toDate: toISODate(customTo),
        periodLabel: `${customFrom.toLocaleDateString()} – ${customTo.toLocaleDateString()}`,
      };
    }
    return { fromDate: null, toDate: null, periodLabel: null };
  }, [mode, selectedQuarter, selectedMonth, customFrom, customTo]);

  const endpoint =
    fromDate && toDate
      ? `${config.apiBaseUrl}/sales/scorecard/${ccode}/pricingQTD/detail?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`
      : null;

  const { data, isLoading, error } = useAuthenticatedData(endpoint, { deps: [endpoint] });

  const { data: salesPersonName } = useAuthenticatedData(
    `${config.apiBaseUrl}/data/name?ccode=${ccode}`
  );

  const [searchTerm, setSearchTerm] = useState('');

  const formatCurrency = (value) => {
    if (!value || value === 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '';
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '');

  const getRowColor = (colorCategory, isTotal = false) => {
    if (isTotal) return '#e0e0e0';
    switch (colorCategory?.toLowerCase()) {
      case 'red':    return '#D88C8C';
      case 'green':  return '#73A57D';
      case 'yellow': return '#D1A752';
      default:       return undefined;
    }
  };

  const calculateTotals = (rows) => {
    if (!rows || rows.length === 0) return null;
    const totals = {
      id: 'TOTAL', accountName: 'TOTAL',
      qty: 0, unitPrice: 0, discountAmount: 0,
      extendedSalePrice: 0, pentagonSellPrice: 0,
      amountDifference: 0, percentDifference: 0,
    };
    rows.forEach((row) => {
      totals.qty               += row.qty               || 0;
      totals.discountAmount    += row.discountAmount    || 0;
      totals.extendedSalePrice += row.extendedSalePrice || 0;
      totals.pentagonSellPrice += row.pentagonSellPrice || 0;
      totals.amountDifference  += row.amountDifference  || 0;
    });
    totals.percentDifference = totals.pentagonSellPrice
      ? (totals.extendedSalePrice / totals.pentagonSellPrice) * 100 - 100
      : 0;
    return totals;
  };

  const filteredData = useMemo(() => {
    if (!data || !searchTerm) return data || [];
    const term = searchTerm.toLowerCase();
    return data.filter(
      (row) =>
        row.accountName?.toLowerCase().includes(term) ||
        row.partnumber?.toLowerCase().includes(term) ||
        row.marketSegment?.toLowerCase().includes(term) ||
        row.tag?.toLowerCase().includes(term) ||
        row.ata?.toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  const exportToCSV = () => {
    if (!filteredData || !filteredData.length) return;
    const safeName = (salesPersonName || ccode).replace(/[^a-zA-Z0-9]/g, '-');
    const filename = `pricing-${safeName}-${fromDate}-to-${toDate}`;
    const headers = [
      'Salesperson', 'Period',
      'Account', 'Market Segment', 'Date', 'Partnumber', 'Description', 'Qty',
      'Unit Price', 'Discount', 'Extended Price', 'Pentagon Price',
      'Amount Diff', 'Percent Diff', 'Condition', 'Class',
      'Physical WH', 'Virtual WH',
      'SO Created', 'Receiver Date', 'DOS', 'Latest Tag Date', 'Repair Age',
      'Tag', 'ATA', 'ILS EUI',
    ];
    const csvContent = [
      headers.join(','),
      ...filteredData.map((row) =>
        [
          `"${salesPersonName || ccode}"`,
          `"${periodLabel}"`,
          `"${row.accountName || ''}"`,
          `"${row.marketSegment || ''}"`,
          `"${formatDate(row.date)}"`,
          `"${row.partnumber || ''}"`,
          `"${(row.description || '').replace(/"/g, '""')}"`,
          row.qty || 0,
          row.unitPrice || 0,
          row.discountAmount || 0,
          row.extendedSalePrice || 0,
          row.pentagonSellPrice || 0,
          row.amountDifference || 0,
          row.percentDifference || 0,
          `"${row.condition || ''}"`,
          `"${row.class || ''}"`,
          `"${row.physicalWarehouseCode || ''}"`,
          `"${row.virtualWarehouseCode || ''}"`,
          `"${formatDate(row.soAddedOn)}"`,
          `"${formatDate(row.receiverDate)}"`,
          row.dos ?? '',
          `"${formatDate(row.latestTagDate)}"`,
          row.repairAge ?? '',
          `"${row.tag || ''}"`,
          `"${row.ata || ''}"`,
          row.ilsEndUserInquiry ?? '',
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const maxDate = new Date();

  return (
    <Paper shadow="md" radius="md">
      <Stack gap="xs">
        {/* Header */}
        <Box>
          <Group gap="sm" mb="xs" align="center">
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => (window.history.length > 1 ? navigate(-1) : window.close())}
            >
              Back
            </Button>
          </Group>
          <Text
            component="h1"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              color: '#FDB813',
              fontStyle: 'italic',
              fontSize: '1.5rem',
              marginBottom: 0,
              textAlign: 'left',
            }}
          >
            Historical Pricing Details — {salesPersonName}
          </Text>
        </Box>

        {/* Info Cards */}
        <Group gap="xs" align="stretch" style={{ marginBottom: '0.5rem' }}>
          <Box style={{ flex: 1, minWidth: '300px' }}>
            <Card withBorder radius="md" style={{ height: '100%' }}>
              <Text size="sm" fw={500} mb="xs">
                About the Data
              </Text>
              <Text size="xs" c="dimmed">
                Transaction-level pricing detail for {salesPersonName}. Shows pricing for each
                invoice and the difference between the Pentagon price and the actual sale price.
              </Text>
            </Card>
          </Box>
          <ColorLegend />
        </Group>

        {/* Period selector */}
        <Paper withBorder radius="sm" p="sm">
          <Stack gap="sm">
            <Radio.Group value={mode} onChange={setMode}>
              <Group gap="lg">
                <Radio value="quarter" label="Quarter" size="sm" />
                <Radio value="month"   label="Month"   size="sm" />
                <Radio value="custom"  label="Custom"  size="sm" />
              </Group>
            </Radio.Group>

            {mode === 'quarter' && (
              <Select
                value={selectedQuarter}
                onChange={setSelectedQuarter}
                data={quarterOptions}
                style={{ width: '160px' }}
                size="sm"
              />
            )}

            {mode === 'month' && (
              <MonthPickerInput
                value={selectedMonth}
                onChange={setSelectedMonth}
                maxDate={maxDate}
                style={{ width: '180px' }}
                size="sm"
                placeholder="Pick a month"
              />
            )}

            {mode === 'custom' && (
              <Group gap="sm" align="flex-end">
                <DatePickerInput
                  label="From"
                  value={customFrom}
                  onChange={setCustomFrom}
                  maxDate={customTo || maxDate}
                  style={{ width: '160px' }}
                  size="sm"
                  placeholder="Start date"
                  clearable
                />
                <DatePickerInput
                  label="To"
                  value={customTo}
                  onChange={setCustomTo}
                  minDate={customFrom || undefined}
                  maxDate={maxDate}
                  style={{ width: '160px' }}
                  size="sm"
                  placeholder="End date"
                  clearable
                />
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Toolbar */}
        <Group justify="space-between" mb="xs">
          <TextInput
            placeholder="Search accounts, parts, segments, tags, ATA..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size={16} />}
            style={{ width: '350px' }}
            size="sm"
          />
          <Group gap="xs">
            <Button
              variant="light"
              size="xs"
              leftSection={<IconExternalLink size={14} />}
              onClick={() => navigate(`/sales/manager/invoices?ccode=${ccode}`)}
            >
              Adjustments
            </Button>
            <ActionIcon
              variant="default"
              onClick={exportToCSV}
              title="Export to CSV"
              size="sm"
              disabled={!filteredData || filteredData.length === 0}
            >
              <IconDownload size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Content */}
        {!endpoint ? (
          <Alert color="blue" icon={<IconInfoCircle size={16} />}>
            Select a start and end date to load data.
          </Alert>
        ) : isLoading ? (
          <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <Loader />
          </Box>
        ) : error ? (
          <Alert color="red" title="Error">
            {error.message}
          </Alert>
        ) : filteredData.length === 0 ? (
          <Alert color="blue" icon={<IconInfoCircle size={16} />}>
            No pricing data found for {periodLabel}.
          </Alert>
        ) : (
          <>
            <Paper withBorder style={{ height: '500px' }}>
              <ScrollArea style={{ height: '100%' }}>
                <Table striped highlightOnHover size="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '200px' }}>Account</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Market Segment</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Date</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Partnumber</Table.Th>
                      <Table.Th style={{ width: '60px' }}>Qty</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Unit Price</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Discount</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Extended Price</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Pentagon Price</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Amount Diff</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Percent Diff</Table.Th>
                      <Table.Th style={{ width: '80px' }}>Condition</Table.Th>
                      <Table.Th style={{ width: '80px' }}>Class</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Physical WH</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Virtual WH</Table.Th>
                      <Table.Th style={{ width: '100px' }}>SO Created</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Receiver Date</Table.Th>
                      <Table.Th style={{ width: '70px' }}>DOS</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Latest Tag Date</Table.Th>
                      <Table.Th style={{ width: '80px' }}>Repair Age</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Tag</Table.Th>
                      <Table.Th style={{ width: '60px' }}>ATA</Table.Th>
                      <Table.Th style={{ width: '70px' }}>ILS EUI</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {[...filteredData, calculateTotals(filteredData)].filter(Boolean).map((row) => (
                      <Table.Tr
                        key={row.id}
                        style={{
                          backgroundColor: getRowColor(row.colorCategory, row.id === 'TOTAL'),
                          fontWeight: row.id === 'TOTAL' ? 'bold' : 'normal',
                          cursor: row.id !== 'TOTAL' && row.docNo && row.line != null ? 'pointer' : undefined,
                        }}
                        onClick={() => {
                          if (row.id !== 'TOTAL' && row.docNo && row.line != null)
                            navigate(`/sales/manager/invoices/${encodeURIComponent(row.docNo)}/${row.line}`);
                        }}
                      >
                        <Table.Td><Text size="xs" fw={row.id === 'TOTAL' ? 700 : 500} c="dark">{row.accountName}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.marketSegment}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{formatDate(row.date)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.partnumber}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{row.qty}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.unitPrice)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.discountAmount)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.extendedSalePrice)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.pentagonSellPrice)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.amountDifference)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatPercent(row.percentDifference)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.condition}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.class}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.physicalWarehouseCode}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.virtualWarehouseCode}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{formatDate(row.soAddedOn)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{formatDate(row.receiverDate)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{row.dos ?? ''}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{formatDate(row.latestTagDate)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{row.repairAge ?? ''}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.tag}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.ata}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{row.ilsEndUserInquiry ?? ''}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>
            <Text size="xs" c="dimmed">
              Showing {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
            </Text>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default SalesPerformanceScorecardHistoricalPricingDetail;